/**
 * PSP/CRE Tool v3.0 - Scenario History Manager
 *
 * Provides undo/redo functionality for scenario changes
 * Maintains history of last 20 scenario states
 *
 * ENHANCED VERSION - Adds checkpoints, change detection, and advanced features
 *
 * Author: Dr. Tatar Radu MD
 * Date: November 26, 2025
 *
 * F1-FIX (April 2026): psp_cre_history now stored with LZ-string compression.
 * 20 × full scenario snapshots uncompressed = up to 14 MB (corps scenario).
 * lsWrite/lsRead achieve 65–75% reduction, keeping history within quota.
 * Backward compatibility: lsRead falls back to plain JSON for legacy entries.
 */

import { lsWrite, lsRead } from '../utils/lsStore.js';

class ScenarioHistory {
  constructor(maxHistory = 20) {
    this.history = [];
    this.currentIndex = -1;
    this.maxHistory = maxHistory;
    
    // Try to load history from localStorage
    this.loadFromStorage();
  }

  /**
   * Add a new scenario state to history
   * @param {Object} scenario - The scenario object to save
   */
  push(scenario) {
    // Remove any "future" states if we're not at the end
    // (happens when user makes changes after undo)
    if (this.currentIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.currentIndex + 1);
    }

    // Deep clone the scenario to prevent reference issues
    const scenarioCopy = JSON.parse(JSON.stringify(scenario));
    
    // NEW: Add timestamp
    scenarioCopy._history_timestamp = new Date().toISOString();

    // Add new state
    this.history.push(scenarioCopy);

    // Limit history size (keep only last N states)
    if (this.history.length > this.maxHistory) {
      this.history.shift();
    } else {
      this.currentIndex++;
    }

    // Save to localStorage
    this.saveToStorage();
  }

  /**
   * Undo to previous state
   * @returns {Object|null} Previous scenario or null if can't undo
   */
  undo() {
    if (!this.canUndo()) {
      console.warn('Cannot undo: already at oldest state');
      return null;
    }

    this.currentIndex--;
    const previousScenario = this.history[this.currentIndex];
    
    // Save to localStorage
    this.saveToStorage();
    
    // Return deep clone to prevent mutations
    return JSON.parse(JSON.stringify(previousScenario));
  }

  /**
   * Redo to next state
   * @returns {Object|null} Next scenario or null if can't redo
   */
  redo() {
    if (!this.canRedo()) {
      console.warn('Cannot redo: already at newest state');
      return null;
    }

    this.currentIndex++;
    const nextScenario = this.history[this.currentIndex];
    
    // Save to localStorage
    this.saveToStorage();
    
    // Return deep clone to prevent mutations
    return JSON.parse(JSON.stringify(nextScenario));
  }

  /**
   * Check if undo is possible
   * @returns {boolean}
   */
  canUndo() {
    return this.currentIndex > 0;
  }

  /**
   * Check if redo is possible
   * @returns {boolean}
   */
  canRedo() {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Get current scenario
   * @returns {Object|null}
   */
  getCurrent() {
    if (this.currentIndex >= 0 && this.currentIndex < this.history.length) {
      return JSON.parse(JSON.stringify(this.history[this.currentIndex]));
    }
    return null;
  }
  
  // ============================================================================
  // NEW: PEEK METHODS (view without changing index)
  // ============================================================================
  
  /**
   * Peek at previous state without undoing (NEW)
   * @returns {Object|null} Previous state or null
   */
  peekPrevious() {
    if (!this.canUndo()) {
      return null;
    }
    return JSON.parse(JSON.stringify(this.history[this.currentIndex - 1]));
  }
  
  /**
   * Peek at next state without redoing (NEW)
   * @returns {Object|null} Next state or null
   */
  peekNext() {
    if (!this.canRedo()) {
      return null;
    }
    return JSON.parse(JSON.stringify(this.history[this.currentIndex + 1]));
  }
  
  // ============================================================================
  // NEW: JUMP TO INDEX
  // ============================================================================
  
  /**
   * Jump to specific index in history (NEW)
   * @param {number} index - Index to jump to
   * @returns {Object|null} State at index or null if invalid
   */
  jumpTo(index) {
    if (index < 0 || index >= this.history.length) {
      console.warn(`Cannot jump to index ${index}: out of range`);
      return null;
    }

    this.currentIndex = index;
    this.saveToStorage();
    
    return JSON.parse(JSON.stringify(this.history[index]));
  }

  /**
   * Clear all history
   */
  clear() {
    this.history = [];
    this.currentIndex = -1;
    this.saveToStorage();
  }

  /**
   * Get history statistics
   * @returns {Object}
   */
  getStats() {
    return {
      totalStates: this.history.length,
      currentIndex: this.currentIndex,
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      undoSteps: this.currentIndex,
      redoSteps: this.history.length - 1 - this.currentIndex,
      // NEW: Enhanced stats
      maxHistory: this.maxHistory,
      oldestTimestamp: this.history.length > 0 
        ? this.history[0]._history_timestamp 
        : null,
      newestTimestamp: this.history.length > 0 
        ? this.history[this.history.length - 1]._history_timestamp 
        : null
    };
  }
  
  // ============================================================================
  // NEW: CHECKPOINT SYSTEM (named save points)
  // ============================================================================
  
  /**
   * Create a checkpoint (named save point) (NEW)
   * @param {Object} scenario - Scenario to checkpoint
   * @param {string} name - Checkpoint name
   */
  checkpoint(scenario, name = null) {
    const checkpointScenario = JSON.parse(JSON.stringify(scenario));
    
    checkpointScenario._checkpoint = true;
    checkpointScenario._checkpoint_name = name || `Checkpoint ${this.history.length}`;
    checkpointScenario._checkpoint_timestamp = new Date().toISOString();
    
    this.push(checkpointScenario);
  }
  
  /**
   * Get all checkpoints in history (NEW)
   * @returns {Array} Array of checkpoint info
   */
  getCheckpoints() {
    return this.history
      .map((state, index) => ({ state, index }))
      .filter(({ state }) => state._checkpoint === true)
      .map(({ state, index }) => ({
        index: index,
        name: state._checkpoint_name,
        timestamp: state._checkpoint_timestamp,
        isCurrent: index === this.currentIndex
      }));
  }
  
  // ============================================================================
  // NEW: CHANGE DETECTION
  // ============================================================================
  
  /**
   * Compare two scenario states to detect changes (NEW)
   * @param {Object} state1 - First state
   * @param {Object} state2 - Second state
   * @returns {Object} Object describing differences
   */
  static compareStates(state1, state2) {
    const changes = {
      has_changes: false,
      changed_sections: [],
      details: {}
    };

    // Compare major sections
    const sections = [
      'force',
      'force_composition',
      'operation_parameters',
      'operations_config',
      'medical_configuration',
      'medical_config',
      'logistics_config'
    ];

    sections.forEach(section => {
      if (state1[section] || state2[section]) {
        const json1 = JSON.stringify(state1[section]);
        const json2 = JSON.stringify(state2[section]);
        
        if (json1 !== json2) {
          changes.has_changes = true;
          changes.changed_sections.push(section);
          changes.details[section] = 'modified';
        }
      }
    });

    return changes;
  }
  
  /**
   * Get description of changes between current and previous state (NEW)
   * @returns {Object|null} Changes description or null
   */
  getChangesSinceLastState() {
    const current = this.getCurrent();
    const previous = this.peekPrevious();
    
    if (!current || !previous) {
      return null;
    }

    return ScenarioHistory.compareStates(previous, current);
  }

  /**
   * Save history to localStorage
   */
  saveToStorage() {
    try {
      const historyData = {
        history: this.history,
        currentIndex: this.currentIndex,
        timestamp: new Date().toISOString(),
        maxHistory: this.maxHistory  // NEW: Save max history setting
      };
      
      // F1-FIX: lsWrite compresses before storage (65–75% reduction).
      // If quota is still exceeded, trim to last 5 states and retry.
      const ok = lsWrite('psp_cre_history', historyData);
      if (!ok) {
        this.history = this.history.slice(-5);
        this.currentIndex = Math.min(this.currentIndex, this.history.length - 1);
        lsWrite('psp_cre_history', {
          history: this.history,
          currentIndex: this.currentIndex,
          timestamp: new Date().toISOString(),
          maxHistory: this.maxHistory
        });
      }

    } catch (error) {
      console.error('Failed to save history to localStorage:', error);
    }
  }

  /**
   * Load history from localStorage
   */
  loadFromStorage() {
    try {
      // F1-FIX: lsRead decompresses; falls back to plain JSON for legacy data.
      const historyData = lsRead('psp_cre_history');
      
      // lsRead returns already-parsed object (not a raw string)
      if (historyData && historyData.history && Array.isArray(historyData.history)) {
        this.history = historyData.history;
        this.currentIndex = historyData.currentIndex || -1;

        // Restore max history setting if saved
        if (historyData.maxHistory) {
          this.maxHistory = historyData.maxHistory;
        }
      }
      
    } catch (error) {
      console.error('Failed to load history from localStorage:', error);
      // If load fails, start fresh
      this.history = [];
      this.currentIndex = -1;
    }
  }
  
  /**
   * Clear history from localStorage (NEW)
   */
  clearFromStorage() {
    try {
      localStorage.removeItem('psp_cre_history');
      this.clear();
      return true;
    } catch (error) {
      console.error('Error clearing history from storage:', error);
      return false;
    }
  }

  /**
   * Export history for debugging
   * @returns {Object}
   */
  exportHistory() {
    return {
      exported_at: new Date().toISOString(),  // NEW
      version: '3.0',  // NEW
      history: this.history,
      currentIndex: this.currentIndex,
      maxHistory: this.maxHistory,  // NEW
      stats: this.getStats()
    };
  }

  /**
   * Import history (for debugging or restoration)
   * @param {Object} historyData
   */
  importHistory(historyData) {
    try {
      if (historyData.history && Array.isArray(historyData.history)) {
        this.history = historyData.history;
        this.currentIndex = historyData.currentIndex !== undefined 
          ? historyData.currentIndex 
          : this.history.length - 1;
        
        // NEW: Restore max history if provided
        if (historyData.maxHistory) {
          this.maxHistory = historyData.maxHistory;
        }
        
        this.saveToStorage();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to import history:', error);
      return false;
    }
  }
  
  // ============================================================================
  // NEW: ADDITIONAL UTILITY METHODS
  // ============================================================================
  
  /**
   * Get all history states (for debugging/export) (NEW)
   * @returns {Array} Array of all history states
   */
  getAllStates() {
    return JSON.parse(JSON.stringify(this.history));
  }
  
  /**
   * Get history size (NEW)
   * @returns {number} Number of states in history
   */
  size() {
    return this.history.length;
  }
  
  /**
   * Get current position in history (NEW)
   * @returns {number} Current index
   */
  getCurrentIndex() {
    return this.currentIndex;
  }
}

export default ScenarioHistory;
